import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import client from '../api/client';
import { C, Toast, useNotify } from '../utils/styles';

// ── Business types ─────────────────────────────────────────────
const BUSINESS_TYPES = [
  { id:'digital_agency',  label:'Digital Agency',          icon:'ti-device-laptop',  desc:'SEO, Web Dev, Social Media, Performance Marketing' },
  { id:'healthcare',      label:'Healthcare & Wellness',    icon:'ti-heart-plus',     desc:'Clinics, Hospitals, Health Coaches, Wellness Centres' },
  { id:'real_estate',     label:'Real Estate & Properties', icon:'ti-building',       desc:'Agents, Builders, Property Management' },
  { id:'academy',         label:'Academy & Institution',    icon:'ti-school',         desc:'Online Courses, Coaching, Training, Schools' },
  { id:'ecommerce',       label:'E-commerce & Retail',      icon:'ti-shopping-cart',  desc:'Online Stores, Retail, D2C Brands' },
  { id:'finance',         label:'Finance & Consulting',     icon:'ti-chart-line',     desc:'CAs, Financial Advisors, Business Consultants' },
  { id:'restaurant',      label:'Restaurant & Food',        icon:'ti-chef-hat',       desc:'Restaurants, Cafes, Food Delivery, Catering' },
  { id:'freelancer',      label:'Freelancer / Solopreneur', icon:'ti-user-circle',    desc:'Independent professionals working solo' },
  { id:'other',           label:'Other',                    icon:'ti-dots-circle-horizontal', desc:'My business doesn\'t fit the above categories' },
];

// ── Goals per business type ─────────────────────────────────────
const GOALS_BY_TYPE = {
  digital_agency: [
    'Generate & manage client leads',
    'Email marketing campaigns',
    'Automate client follow-ups',
    'Client onboarding & management',
    'Pipeline & deal tracking',
    'Invoice & billing clients',
    'Team collaboration',
    'Analytics & performance reports',
    'Calendar & meeting booking',
    'White-label for my clients',
  ],
  healthcare: [
    'Appointment & calendar booking',
    'Patient follow-up automation',
    'Send health newsletters',
    'Manage patient leads',
    'Staff scheduling',
    'Invoice patients / billing',
    'Online consultation booking',
    'Automated reminders',
  ],
  real_estate: [
    'Capture & manage property leads',
    'Client follow-up automation',
    'Email property listings',
    'Pipeline tracking (buyer/seller)',
    'Calendar booking for site visits',
    'Invoice & billing clients',
    'Analytics & reporting',
    'Team collaboration',
  ],
  academy: [
    'Student enrollment & management',
    'Email campaigns for courses',
    'Automate student follow-ups',
    'Session & class booking',
    'Invoice students / billing',
    'Lead generation for courses',
    'Team & instructor management',
    'Analytics & reporting',
  ],
  ecommerce: [
    'Email marketing campaigns',
    'Abandoned cart recovery',
    'Customer follow-up automation',
    'Lead generation',
    'Analytics & sales reporting',
    'Invoice & billing',
    'Team collaboration',
  ],
  finance: [
    'Client lead management',
    'Invoice & billing clients',
    'Calendar booking for consultations',
    'Automated follow-ups',
    'Email campaigns',
    'Pipeline & deal tracking',
    'Analytics & reporting',
    'Team collaboration',
  ],
  restaurant: [
    'Table & event booking',
    'Email marketing to customers',
    'Customer follow-up & loyalty',
    'Lead generation',
    'Staff scheduling',
    'Invoice & billing',
    'Analytics & reporting',
  ],
  freelancer: [
    'Invoice & bill clients',
    'Project & client tracking',
    'Calendar booking',
    'Email outreach campaigns',
    'Automate follow-ups',
    'Lead generation',
    'Analytics & reporting',
  ],
  other: [
    'Manage leads & contacts',
    'Email marketing campaigns',
    'Automate follow-ups',
    'Invoice & billing',
    'Calendar & booking',
    'Pipeline & deal tracking',
    'Team collaboration',
    'Analytics & reporting',
    'White-label for clients',
  ],
};

const TEAM_SIZES = [
  { id:'1',     label:'Just me',    icon:'ti-user'       },
  { id:'2-5',   label:'2 – 5',     icon:'ti-users'      },
  { id:'6-15',  label:'6 – 15',    icon:'ti-users-group'},
  { id:'16-50', label:'16 – 50',   icon:'ti-building'   },
  { id:'50+',   label:'50+',       icon:'ti-building-community'},
];

const HEAR_FROM = [
  'Google Search',
  'LinkedIn',
  'Facebook / Instagram',
  'YouTube',
  'Friend / Colleague',
  'WhatsApp',
  'Email Newsletter',
  'Event / Webinar',
  'Other',
];

export default function Onboarding() {
  const [params]      = useSearchParams();
  const planIdFromUrl = params.get('plan');

  const [step,    setStep]   = useState(0);
  const [plans,   setPlans]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [rzpLoading, setRzpLoading] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [notification, notify] = useNotify();

  const [form, setForm] = useState({
    business_type: '',
    team_size:     '',
    website:       '',
    phone:         '',
    goals:         [],
    hear_from:     '',
    selected_plan: planIdFromUrl || '',
  });

  const STEPS = [
    { id:'business', label:'Your Business', icon:'ti-building'    },
    { id:'goals',    label:'Goals',         icon:'ti-target'      },
    { id:'plan',     label:'Choose Plan',   icon:'ti-credit-card' },
    { id:'payment',  label:'Payment',       icon:'ti-lock'        },
  ];

  useEffect(() => {
    client.get('/api/accounts/plans/all')
      .then(({ data }) => {
        const allPlans = data || [];
        setPlans(allPlans);
        // Pre-select plan from URL
        if (planIdFromUrl && allPlans.find(p => p.id === planIdFromUrl)) {
          setForm(f => ({ ...f, selected_plan: planIdFromUrl }));
        } else if (!planIdFromUrl && allPlans.length > 0) {
          // Default to cheapest plan
          const sorted = [...allPlans].sort((a,b) => (a.price_monthly_inr||0)-(b.price_monthly_inr||0));
          setForm(f => ({ ...f, selected_plan: sorted[0].id }));
        }
      })
      .catch(() => {
        client.get('/api/billing/plans/public')
          .then(({ data }) => setPlans(data || []))
          .catch(() => {});
      });
  }, [planIdFromUrl]);

  const goalsForType = form.business_type ? (GOALS_BY_TYPE[form.business_type] || GOALS_BY_TYPE.other) : [];
  const toggleGoal   = (g) => setForm(f => ({ ...f, goals: f.goals.includes(g) ? f.goals.filter(x=>x!==g) : [...f.goals,g] }));
  const selectedPlan = plans.find(p => p.id === form.selected_plan);

  const canNext = [
    !!form.business_type && !!form.team_size,
    form.goals.length >= 2,
    !!form.selected_plan,
    paymentDone,  // step 3: must complete payment
  ];

  // ── Razorpay payment handler ─────────────────────────────
  const handleRazorpay = async () => {
    if (!selectedPlan || !selectedPlan.price_monthly_inr) return;
    setRzpLoading(true);
    try {
      const { data: order } = await client.post('/api/billing/razorpay/create-order', {
        plan_id:  form.selected_plan,
        amount:   100, // ₹1 in paise — card verification charge
        type:     'verification',
      });
      const options = {
        key:         order.key_id,
        amount:      order.amount,
        currency:    'INR',
        name:        'Digital Xtension',
        description: `${selectedPlan.name} — Card verification (₹1)`,
        order_id:    order.order_id,
        handler: async (response) => {
          try {
            await client.post('/api/billing/razorpay/verify', {
              order_id:   order.order_id,
              payment_id: response.razorpay_payment_id,
              signature:  response.razorpay_signature,
              plan_id:    form.selected_plan,
              type:       'verification',
            });
            setPaymentDone(true);
            notify('✅ Payment method verified! Your trial starts now.');
          } catch (err) {
            notify(err.response?.data?.error || 'Verification failed','error');
          }
          setRzpLoading(false);
        },
        prefill:   { name:'', email:'' },
        theme:     { color: '#0099FF' },
        modal:     { ondismiss: () => setRzpLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to initiate payment','error');
      setRzpLoading(false);
    }
  };

  const handleFinish = async () => {
    if (!form.selected_plan) return notify('Please select a plan','error');
    setSaving(true);
    try {
      const { data } = await client.post('/api/billing/start-trial', {
        plan_id:       form.selected_plan,
        business_type: form.business_type,
        team_size:     form.team_size,
        website:       form.website,
        phone:         form.phone,
        goals:         form.goals,
        hear_from:     form.hear_from,
      });
      // Use window.location.href (not navigate) so AuthContext fully re-initializes
      // Redirect to correct dashboard based on account type
      const accountType = data?.account_type || 'sub';
      if (accountType === 'agency') {
        window.location.href = '/agency/accounts';
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to start trial','error');
      setSaving(false);
    }
  };

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const pf  = (n) => `₹${Number(n||0).toLocaleString()}`;

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f9ff 0%,#f9fafb 100%)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <Toast {...notification}/>
      <div style={{ width:'100%', maxWidth:640 }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:26, fontWeight:800, color:C.primary, marginBottom:4 }}>Digital Xtension</div>
          <div style={{ fontSize:14, color:'#6b7280' }}>Let's set up your account — takes less than 2 minutes</div>
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0, marginBottom:24 }}>
          {STEPS.map((s,i) => (
            <React.Fragment key={s.id}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:100 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                  background: i<step?'#22c55e':i===step?C.primary:'#e5e7eb',
                  color: i<=step?'#fff':'#9ca3af', fontSize:14, fontWeight:700, transition:'all 0.2s' }}>
                  {i<step ? <i className="ti ti-check" style={{fontSize:16}}/> : i+1}
                </div>
                <span style={{ fontSize:11, fontWeight:i===step?600:400, color:i===step?C.primary:i<step?'#22c55e':'#9ca3af', whiteSpace:'nowrap' }}>{s.label}</span>
              </div>
              {i<STEPS.length-1 && <div style={{ flex:1, height:2, background:i<step?'#22c55e':'#e5e7eb', margin:'0 4px', marginBottom:16 }}/>}
            </React.Fragment>
          ))}
        </div>

        <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 4px 24px rgba(0,0,0,0.08)', padding:28 }}>

          {/* ── STEP 0: Business info ── */}
          {step===0 && (
            <div style={{ display:'grid', gap:20 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:'#111827', marginBottom:4 }}>Tell us about your business</div>
                <div style={{ fontSize:13, color:'#6b7280' }}>This helps us personalise your experience.</div>
              </div>

              {/* Business type */}
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:10, display:'block' }}>Business Type *</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {BUSINESS_TYPES.map(t => (
                    <button key={t.id} onClick={() => setForm(f => ({ ...f, business_type:t.id, goals:[] }))}
                      style={{ padding:'12px 10px', borderRadius:10, border:`1px solid ${form.business_type===t.id?C.primary:'#e5e7eb'}`, background:form.business_type===t.id?'var(--color-primary-light)':'#fff', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}>
                      <i className={`ti ${t.icon}`} style={{ fontSize:20, color:form.business_type===t.id?C.primary:'#9ca3af', display:'block', marginBottom:4 }}/>
                      <div style={{ fontSize:11, fontWeight:600, color:form.business_type===t.id?C.primary:'#374151', lineHeight:1.3 }}>{t.label}</div>
                    </button>
                  ))}
                </div>
                {form.business_type && (
                  <div style={{ fontSize:11, color:C.primary, marginTop:6 }}>
                    <i className="ti ti-info-circle" style={{ marginRight:4 }}/>
                    {BUSINESS_TYPES.find(t=>t.id===form.business_type)?.desc}
                  </div>
                )}
              </div>

              {/* Team size */}
              <div>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:10, display:'block' }}>Team Size *</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {TEAM_SIZES.map(s => (
                    <button key={s.id} onClick={() => setForm(f => ({ ...f, team_size:s.id }))}
                      style={{ padding:'8px 16px', borderRadius:8, border:`1px solid ${form.team_size===s.id?C.primary:'#e5e7eb'}`, background:form.team_size===s.id?'var(--color-primary-light)':'#fff', color:form.team_size===s.id?C.primary:'#374151', fontSize:13, fontWeight:form.team_size===s.id?600:400, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                      <i className={`ti ${s.icon}`} style={{ fontSize:14 }}/>{s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Website + Phone */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:12, fontWeight:500, color:'#374151', marginBottom:4, display:'block' }}>Website <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional)</span></label>
                  <input style={inp} placeholder="https://yourbusiness.com" value={form.website} onChange={e=>setForm(f=>({...f,website:e.target.value}))}/>
                </div>
                <div>
                  <label style={{ fontSize:12, fontWeight:500, color:'#374151', marginBottom:4, display:'block' }}>Phone <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional)</span></label>
                  <input style={inp} placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/>
                </div>
              </div>

              {/* How did you hear about us */}
              <div>
                <label style={{ fontSize:12, fontWeight:500, color:'#374151', marginBottom:6, display:'block' }}>How did you hear about Digital Xtension? <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional)</span></label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {HEAR_FROM.map(h => (
                    <button key={h} onClick={() => setForm(f => ({ ...f, hear_from: f.hear_from===h?'':h }))}
                      style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${form.hear_from===h?C.primary:'#e5e7eb'}`, background:form.hear_from===h?'var(--color-primary-light)':'#fff', color:form.hear_from===h?C.primary:'#6b7280', fontSize:12, cursor:'pointer', fontWeight:form.hear_from===h?600:400 }}>
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 1: Goals ── */}
          {step===1 && (
            <div style={{ display:'grid', gap:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:'#111827', marginBottom:4 }}>
                  What do you want to achieve?
                </div>
                <div style={{ fontSize:13, color:'#6b7280' }}>
                  Select at least 2 goals — we'll configure your account around them.
                </div>
              </div>
              {!form.business_type ? (
                <div style={{ textAlign:'center', padding:20, color:'#9ca3af', fontSize:13 }}>
                  <i className="ti ti-arrow-left" style={{ marginRight:6 }}/>Go back and select your business type first
                </div>
              ) : (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {goalsForType.map(g => {
                      const active = form.goals.includes(g);
                      return (
                        <button key={g} onClick={() => toggleGoal(g)}
                          style={{ padding:'12px 14px', borderRadius:10, border:`1px solid ${active?C.primary:'#e5e7eb'}`, background:active?'var(--color-primary-light)':'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:10, textAlign:'left' }}>
                          <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${active?C.primary:'#d1d5db'}`, background:active?C.primary:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            {active && <i className="ti ti-check" style={{ fontSize:12, color:'#fff' }}/>}
                          </div>
                          <span style={{ fontSize:12, fontWeight:active?600:400, color:active?C.primary:'#374151', lineHeight:1.4 }}>{g}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize:12, color:form.goals.length>=2?'#22c55e':'#9ca3af', display:'flex', alignItems:'center', gap:6 }}>
                    <i className={`ti ${form.goals.length>=2?'ti-circle-check':'ti-info-circle'}`}/>
                    {form.goals.length} selected {form.goals.length<2?'— select at least 2':'— great!'}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP 2: Plan selection ── */}
          {step===2 && (
            <div style={{ display:'grid', gap:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:'#111827', marginBottom:4 }}>
                  {selectedPlan ? 'Your Selected Plan' : 'Choose your plan'}
                </div>
                <div style={{ fontSize:13, color:'#6b7280' }}>
                  Start free — no payment required during your trial. Card saved for after trial ends.
                </div>
              </div>

              <div style={{ display:'grid', gap:10 }}>
                {plans.map(p => {
                  const active  = form.selected_plan === p.id;
                  const savings = p.price_yearly_inr && p.price_monthly_inr
                    ? Math.round(((p.price_monthly_inr*12 - p.price_yearly_inr) / (p.price_monthly_inr*12))*100) : 0;
                  return (
                    <button key={p.id} onClick={() => setForm(f => ({ ...f, selected_plan:p.id }))}
                      style={{ padding:'16px 18px', borderRadius:12, border:`2px solid ${active?C.primary:'#e5e7eb'}`, background:active?'var(--color-primary-light)':'#fff', cursor:'pointer', textAlign:'left', display:'flex', gap:14, alignItems:'flex-start' }}>
                      <div style={{ marginTop:2, width:20, height:20, borderRadius:'50%', border:`2px solid ${active?C.primary:'#d1d5db'}`, background:active?C.primary:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {active && <div style={{ width:8, height:8, borderRadius:'50%', background:'#fff' }}/>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                          <span style={{ fontWeight:700, fontSize:15, color:active?C.primary:'#111827' }}>{p.name}</span>
                          {(p.trial_days||0)>0 && (
                            <span style={{ background:'#f0fdf4', color:'#16a34a', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:600, border:'1px solid #bbf7d0' }}>
                              {p.trial_days}-day free trial
                            </span>
                          )}
                          {p.is_popular && <span style={{ background:'#f59e0b', color:'#fff', borderRadius:20, padding:'1px 8px', fontSize:10, fontWeight:700 }}>POPULAR</span>}
                        </div>
                        {p.description && <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>{p.description}</div>}
                        <div style={{ display:'flex', alignItems:'baseline', gap:6, flexWrap:'wrap' }}>
                          <span style={{ fontSize:20, fontWeight:800, color:active?C.primary:'#111827' }}>
                            {p.price_monthly_inr>0 ? pf(p.price_monthly_inr) : 'Free'}
                          </span>
                          {p.price_monthly_inr>0 && <span style={{ fontSize:12, color:'#6b7280' }}>/month</span>}
                          {savings>0 && <span style={{ fontSize:11, color:'#16a34a', fontWeight:600 }}>or save {savings}% annually</span>}
                        </div>
                        {/* Features */}
                        {p.features && (
                          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:6 }}>
                            {[
                              p.features.contacts_limit!=null && (p.features.contacts_limit===-1?'Unlimited contacts':`${Number(p.features.contacts_limit).toLocaleString()} contacts`),
                              p.features.users_limit!=null    && (p.features.users_limit===-1?'Unlimited users':`${p.features.users_limit} users`),
                              p.sub_account_limit!=null       && (p.sub_account_limit===-1?'Unlimited sub-accounts':`${p.sub_account_limit} sub-account${p.sub_account_limit!==1?'s':''}`),
                              p.features.white_label          && 'White-label',
                              p.features.ai                   && 'AI features',
                            ].filter(Boolean).map((f,i) => (
                              <span key={i} style={{ fontSize:11, color:'#6b7280', display:'flex', alignItems:'center', gap:3 }}>
                                <i className="ti ti-check" style={{ color:'#22c55e', fontSize:11 }}/>{f}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedPlan && (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 16px', fontSize:13, color:'#166534', display:'flex', alignItems:'center', gap:8 }}>
                  <i className="ti ti-shield-check" style={{ fontSize:18, flexShrink:0 }}/>
                  <div>
                    <strong>{selectedPlan.trial_days||14}-day free trial</strong> — no payment charged today.
                    After your trial, you'll be charged <strong>{pf(selectedPlan.price_monthly_inr)}/month</strong>. Cancel anytime.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

          {/* ── STEP 3: Payment ── */}
          {step===3 && (
            <div style={{ display:'grid', gap:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:'#111827', marginBottom:4 }}>
                  Set up your payment method
                </div>
                <div style={{ fontSize:13, color:'#6b7280' }}>
                  We'll verify your card with a ₹1 charge (refunded instantly). Your trial starts now — you won't be billed until it ends.
                </div>
              </div>

              {/* Plan summary */}
              {selectedPlan && (
                <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:12, padding:'16px 18px' }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0369a1', marginBottom:8 }}>
                    <i className="ti ti-crown" style={{ marginRight:6 }}/>{selectedPlan.name} Plan
                  </div>
                  <div style={{ fontSize:13, color:'#0369a1', lineHeight:2 }}>
                    📅 Free trial: <strong>{selectedPlan.trial_days || 14} days</strong><br/>
                    💳 After trial: <strong>
                      {selectedPlan.price_monthly_inr > 0
                        ? `₹${Number(selectedPlan.price_monthly_inr).toLocaleString('en-IN')}/month`
                        : 'Free'}
                    </strong><br/>
                    🔄 Cancel anytime — no lock-in
                  </div>
                </div>
              )}

              {/* Payment button */}
              {!paymentDone ? (
                <div>
                  <button onClick={handleRazorpay} disabled={rzpLoading}
                    style={{ width:'100%', padding:'14px', borderRadius:10, border:'none',
                      background: rzpLoading ? '#e5e7eb' : '#0099FF',
                      color: rzpLoading ? '#9ca3af' : '#fff',
                      fontWeight:700, fontSize:14, cursor: rzpLoading ? 'not-allowed' : 'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:10 }}>
                    <i className={`ti ${rzpLoading ? 'ti-loader ti-spin' : 'ti-credit-card'}`}/>
                    {rzpLoading ? 'Opening payment...' : 'Add Payment Method (₹1 verification)'}
                  </button>
                  <div style={{ fontSize:12, color:'#9ca3af', textAlign:'center', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                    <i className="ti ti-shield-check" style={{ color:'#22c55e', fontSize:14 }}/>
                    Secured by Razorpay · ₹1 refunded instantly · No charges during trial
                  </div>
                </div>
              ) : (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'16px 18px', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:'50%', background:'#22c55e', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <i className="ti ti-check" style={{ fontSize:20, color:'#fff' }}/>
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#166534' }}>Payment method verified!</div>
                    <div style={{ fontSize:12, color:'#16a34a', marginTop:2 }}>
                      Your {selectedPlan?.trial_days || 14}-day free trial starts now. You'll be notified before any charge.
                    </div>
                  </div>
                </div>
              )}

              <div style={{ background:'#f9fafb', borderRadius:10, padding:'12px 14px', fontSize:12, color:'#6b7280' }}>
                <div style={{ fontWeight:600, marginBottom:6, color:'#374151' }}>How it works:</div>
                <div style={{ lineHeight:2 }}>
                  1. We verify your card with a ₹1 refundable charge<br/>
                  2. Your {selectedPlan?.trial_days || 14}-day free trial begins immediately<br/>
                  3. You'll get a reminder 3 days before trial ends<br/>
                  4. After trial, {selectedPlan?.price_monthly_inr > 0 ? `₹${Number(selectedPlan?.price_monthly_inr || 0).toLocaleString('en-IN')}/month` : 'free'} is charged automatically<br/>
                  5. Cancel anytime from your Billing page
                </div>
              </div>
            </div>
          )}

        {/* Navigation */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
          <button onClick={() => setStep(s => s-1)} disabled={step===0}
            style={{ padding:'10px 20px', borderRadius:8, border:'1px solid #e5e7eb', background:'#fff', color:'#374151', fontSize:13, fontWeight:500, cursor:step===0?'not-allowed':'pointer', opacity:step===0?0:1, display:'flex', alignItems:'center', gap:6 }}>
            <i className="ti ti-arrow-left"/> Back
          </button>
          {step < STEPS.length-1 ? (
            <button onClick={() => setStep(s => s+1)} disabled={!canNext[step]}
              style={{ padding:'10px 24px', borderRadius:8, border:'none', background:canNext[step]?C.primary:'#e5e7eb', color:canNext[step]?'#fff':'#9ca3af', fontSize:13, fontWeight:600, cursor:canNext[step]?'pointer':'not-allowed', display:'flex', alignItems:'center', gap:6 }}>
              Next <i className="ti ti-arrow-right"/>
            </button>
          ) : (
            <button onClick={handleFinish} disabled={saving || !form.selected_plan}
              style={{ padding:'10px 24px', borderRadius:8, border:'none', background:saving||!form.selected_plan?'#e5e7eb':C.primary, color:saving||!form.selected_plan?'#9ca3af':'#fff', fontSize:13, fontWeight:600, cursor:saving||!form.selected_plan?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:8 }}>
              <i className={`ti ${saving?'ti-loader ti-spin':'ti-rocket'}`}/>
              {saving ? 'Setting up your account...' : selectedPlan?.trial_days>0 ? `Start ${selectedPlan.trial_days}-Day Free Trial` : 'Get Started'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
